import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AbilityCheck } from '../config/abilities.config';
import { AccessService } from '../services/access.service';
import { AuthenticationService } from '../services/authentication.service';

/**
 * Lets the route open only when the user has ANY of the abilities; otherwise shows the
 * "no access" page. Runs after authGuard (the user and its abilities are already loaded).
 */
export function abilityGuard(check: AbilityCheck): CanActivateFn {
  return async (_route, state) => {
    const access = inject(AccessService);
    const router = inject(Router);
    // Guards of the same route run together: make sure the user (and its abilities) is loaded.
    await inject(AuthenticationService).getUser(true);
    return access.can(check) ? true : router.createUrlTree(['/no-access'], { queryParams: { from: state.url } });
  };
}

/** Dashboard entry: users without the dashboard go to the first page they can open. */
export const homeGuard: CanActivateFn = async (_route, state) => {
  const access = inject(AccessService);
  const router = inject(Router);
  await inject(AuthenticationService).getUser(true);
  if (access.can('dashboard.view')) return true;
  const home = access.homeRoute();
  return router.parseUrl(home ?? `/no-access?from=${encodeURIComponent(state.url)}`);
};

/** Module root (e.g. "/orders"): redirects to its first page the user can open. */
export function firstAllowedChildGuard(moduleRoute: string): CanActivateFn {
  return async () => {
    const access = inject(AccessService);
    const router = inject(Router);
    await inject(AuthenticationService).getUser(true);
    const child = access.firstAllowedChild(moduleRoute);
    return router.parseUrl(child ?? `/no-access?from=${encodeURIComponent(moduleRoute)}`);
  };
}
